
/* eslint-disable vue/no-mutating-props */
// `client` is the orchestrator's shared form-model object (state.client),
// passed by reference so this step can hydrate it from the returning-client
// lookup. The orchestrator owns it; we mutate its fields in place by design.
import { mapGetters } from 'vuex'
import DobInput from '../parts/DobInput.vue'
import AepReasonPicker from '../parts/AepReasonPicker.vue'
import ReferredByPicker from '../parts/ReferredByPicker.vue'
import GroupFind from '~/components/schedule/GroupFind.vue'
import { FORMER_ICHRA_MESSAGE } from '~/util/former-ichra'
import { isOpenState, openStateMethods, stateNames } from '~/util/open-states'

export default {
    components: { GroupFind, DobInput, AepReasonPicker, ReferredByPicker },
    props: {
        client: { type: Object, required: true },
        slug: { type: String, default: null },
        aep: { type: Boolean, default: false },
        // #679: the orchestrator resolves this once identity clears (a
        // returning-found lookup, or a verified new-client group) — it owns
        // the condition so validate() can reuse the exact same rule.
        showScreener: { type: Boolean, default: false },
        // #727: states where a None of the Above visitor books directly.
        openStates: { type: Array, default: () => [] },
    },
    data () {
        return {
            formerIchraMessage: FORMER_ICHRA_MESSAGE,
            lookupError: '',
            lookupLoading: false,
            eligSubmitting: false,
        }
    },
    computed: {
        // connections tiles ({ value, icon, title, description, sourceType }) —
        // store getter. sourceType is the Salesforce-synced SourceType enum.
        ...mapGetters(['connections', 'usStates']),
        // #679: under aep the lede leads with the screener rather than a
        // duration promise, since duration now depends on the answer.
        lede () {
            if (this.aep) {
                return 'A no-cost call with a licensed advisor from our team. First, a quick check so we route you to the right place.'
            }
            return 'A no-cost 45 minute call with a licensed advisor from our team. First, a quick check so we route you to the right place.'
        },
        canLookup () {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.client.lookupEmail || '') &&
                this.client.lookupDobM &&
                this.client.lookupDobD &&
                String(this.client.lookupDobY || '').length === 4
        },
        groupLevels () {
            if (this.client.connection === 'advisor') return ['financial_advisor']
            if (this.client.connection === 'dpc') return ['dpc']
            return ['paying']
        },
        orgNoun () {
            if (this.client.connection === 'advisor') return 'your financial advisor’s firm'
            if (this.client.connection === 'dpc') return 'your doctor'
            if (this.client.connection === 'family') return 'the employer'
            return 'your employer'
        },
        findBtnLabel () {
            if (this.client.connection === 'advisor') return 'Find & Verify Advisor'
            if (this.client.connection === 'dpc') return 'Find & Verify Doctor'
            return 'Find & Verify Employer'
        },
        // #727/#730: the open-state rule, as the backend applies it at /book/ --
        // a non-paying visitor (None of the Above, or a group that does not
        // participate) who lives in an open state.
        openState () {
            const c = this.client
            return (c.connection === 'other' || !!c.groupNonPaying) && isOpenState(c.state, this.openStates)
        },
        stateChosen () { return !!this.client.state },
        openStateName () { return stateNames([this.client.state]) },
        // The None of the Above path has one more question (who referred you);
        // a non-participating group already answers that.
        openStateNoticeSub () {
            return this.client.connection === 'other' ? 'One more quick question before you book.' : 'You can book a phone consultation with us.'
        },
        // ReferredByPicker works on one object; the client keeps flat fields
        // (declared in the orchestrator's newClient() for reactivity).
        referral: {
            get () {
                const c = this.client
                return { type: c.referred_by_type, client_name: c.referred_by_client_name, employer_name: c.referred_by_employer_name, employer_contact: c.referred_by_employer_contact, note: c.referred_by_note }
            },
            set (v) {
                this.$set(this.client, 'referred_by_type', v.type)
                this.$set(this.client, 'referred_by_client_name', v.client_name)
                this.$set(this.client, 'referred_by_employer_name', v.employer_name)
                this.$set(this.client, 'referred_by_employer_contact', v.employer_contact)
                this.$set(this.client, 'referred_by_note', v.note)
                this.$emit('change')
            },
        },
        canSubmitElig () {
            return (this.client.eligName || '').trim().length >= 2 &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.client.eligEmail || '')
        },
        eligFirstName () {
            return ((this.client.eligName || '').trim().split(' ')[0]) || 'there'
        },
        // We authoritatively know this account has no available contact methods.
        methodsResolvedEmpty () {
            const c = this.client
            return !!c.allowedMethodsResolved && !(c.allowed_methods && c.allowed_methods.length)
        },
        // Block message after a returning lookup / verified group with no methods.
        // Group-affiliated clients get the "group doesn't participate" copy; clients
        // with no group on file get the general non-participating copy.
        // The client's group name, whether the lookup hydrated a group object
        // (returning) or GroupFind set source_name + a numeric group id (new).
        groupName () {
            return (this.client.group && this.client.group.name) || this.client.source_name || ''
        },
        blockMessageHtml () {
            // Former Benefit Bay client (closed 2026-10-01): plain text, owner copy.
            // Only on the returning branch -- Yes/No toggling keeps both branches'
            // state, so the flag from a lookup must not caption a new client's group.
            if (this.client.returning === true && this.client.formerIchra) return FORMER_ICHRA_MESSAGE
            const email = '<a href="mailto:help@doctorschoiceusa.com">help@doctorschoiceusa.com</a>'
            if (this.client.group) {
                // #679 F4: under aep, a non-participating group blocks both a
                // consultation and an AEP review, so point them to the
                // MEDICARE/Medicare.gov alternative rather than the generic
                // "email us" copy below.
                if (this.aep) {
                    const subject = this.groupName ? `<strong>${this.groupName}</strong>` : 'Your group'
                    return 'Doctor’s Choice isn’t available through your group yet.<br><br>' +
                        `We’re offered at no cost through participating employers and financial advisor groups. ${subject} doesn’t participate right now, so we can’t schedule a consultation or run an AEP review for you.<br><br>` +
                        'Call 1-800-MEDICARE (1-800-633-4227) or visit Medicare.gov to compare every plan available in your area. Both are free.<br><br>' +
                        'Think this is a mistake, or your group signed up recently? Call us at (800) 656-0894 and we will take another look.'
                }
                const named = this.groupName ? ` <strong>${this.groupName}</strong>` : ''
                return `We’re sorry — it looks like your group${named} does not participate in our program. Feel free to reach out to our team at ${email} if you think this may be an error.`
            }
            return `Unfortunately due to recent insurance carrier changes, Doctor’s Choice is no longer able to provide services to individuals from non-participating employers. If you think this might be an error, or have questions, feel free to email us at ${email} — we’re happy to help!`
        },
    },
    methods: {
        setReturning (v) {
            // Pure view switch: preserve answers in both branches (connection,
            // employer, verified state, and any prior lookup) so toggling Yes/No
            // doesn't discard progress. The template uses v-show to keep the
            // GroupFind mounted and its verified state intact.
            this.client.returning = v
            this.lookupError = ''
            this.$emit('change')
        },
        setConnection (c) {
            const prev = this.client.connection
            this.client.connection = c.value
            this.client.source_type = c.sourceType
            // "employer" and "family" verify the same (paying) employer group,
            // so keep a verified group when switching between them. Switching to
            // a different lookup (advisor) or a no-group option (other) clears it.
            const lookupKey = v => (!v || v === 'other') ? null : (v === 'advisor' || v === 'dpc' ? v : 'employer')
            if (!(lookupKey(prev) && lookupKey(prev) === lookupKey(c.value))) {
                this.client.group = null
                this.client.source_name = ''
                this.client.orgVerified = false
                this.client.groupFormerIchra = false
                // #730: a non-participating group's answer goes with it.
                if (this.client.groupNonPaying) {
                    this.client.allowed_methods = []
                    this.client.allowedMethodsResolved = false
                }
                this.$set(this.client, 'groupNonPaying', false)
                this.$set(this.client, 'groupPayer', false)
            }
            // #727: the state question and the referral answers belong to the
            // None of the Above branch; nothing from it may ride along elsewhere.
            if (prev === 'other' && c.value !== 'other') {
                this.$set(this.client, 'state', '')
                this.client.allowed_methods = []
                this.client.allowedMethodsResolved = false
                this.referral = { type: null, client_name: '', employer_name: '', employer_contact: '', note: '' }
            }
            // ...and entering it re-judges whatever state is already set (About
            // may have set one before the visitor came back), so a previous
            // group's methods never ride along into this branch.
            if (c.value === 'other' && prev !== 'other') {
                this.onHomeState(this.client.state || '')
                return
            }
            this.$emit('change')
        },
        // #727/#730: the "Which state do you live in?" question, on the None of
        // the Above path and under a group that does not participate.
        onHomeState (v) {
            this.$set(this.client, 'state', v ? String(v).toUpperCase() : '')
            this.judgeHomeState()
            this.$emit('change')
        },
        // An open state resolves the methods here (no group to ask). A closed
        // one blocks a non-participating group (resolved, empty -- the block
        // message says so) but only un-resolves the None of the Above path,
        // which shows the eligibility request form instead.
        judgeHomeState () {
            const c = this.client
            if (this.openState) {
                if (c.connection === 'other') c.source_type = 'OTHER'
                c.allowed_methods = openStateMethods(this.aep)
                c.allowedMethodsResolved = true
            } else {
                c.allowed_methods = []
                c.allowedMethodsResolved = !!(c.groupNonPaying && c.state)
            }
        },
        // #730: Find & Verify found the group, but it participates at no level.
        // It still becomes the client's group (that is who referred them); the
        // state question decides whether they can book.
        onNonParticipating (group) {
            this.client.group = group.id
            this.client.source_name = group.name
            this.client.orgVerified = false
            this.client.groupFormerIchra = false
            this.$set(this.client, 'groupNonPaying', true)
            this.judgeHomeState()
            this.$emit('change')
        },
        stateLabel (code) { return stateNames([code]) },
        onGroup (groupId) {
            this.client.group = groupId
        },
        // #679: `aep_reason` isn't declared in the orchestrator's newClient()
        // until Task 2 — Vue 2 requires $set for reactivity on a key that
        // doesn't exist on the object yet.
        onReasonInput (reason) {
            this.$set(this.client, 'aep_reason', reason)
            this.$emit('change')
        },
        async onGroupRaw (group) {
            this.client.groupFormerIchra = false
            this.$set(this.client, 'groupNonPaying', false)
            this.$set(this.client, 'groupPayer', false)
            if (group && group.id) {
                this.client.source_name = group.name
                this.client.orgVerified = true
                this.$set(this.client, 'groupPayer', !!(group.paying || group.financial_advisor))
                this.client.allowed_methods = group.allowed_methods || []
                this.$emit('change')
                // findGroup doesn't include scheduling methods, so fetch the
                // group's allowed methods (mirrors the 1-page flow) — the Time
                // step then only offers what this group permits.
                try {
                    const methods = await this.$api.getGroupSchedulingMethods(group.id, { aep: this.aep })
                    if (Array.isArray(methods)) {
                        this.client.allowed_methods = methods
                        this.client.allowedMethodsResolved = true
                        this.$emit('change')
                    }
                } catch (e) { /* fall back to all methods */ }
            } else {
                this.client.orgVerified = false
                this.$emit('change')
            }
        },
        // D9: the employer the visitor typed is a former Benefit Bay group.
        // GroupFind did not verify it (it no longer participates), so nothing
        // downstream unlocks; the partnership message says why.
        onFormerIchra (group) {
            this.client.group = null
            this.client.source_name = (group && group.name) || ''
            this.client.orgVerified = false
            this.client.groupFormerIchra = true
            this.$set(this.client, 'groupNonPaying', false)
            this.$set(this.client, 'groupPayer', false)
            this.$emit('change')
        },
        async submitElig () {
            if (!this.canSubmitElig || this.eligSubmitting) return
            this.eligSubmitting = true
            try {
                await this.$api.requestEligibilityCheck({
                    name: this.client.eligName,
                    email: this.client.eligEmail,
                    phone: this.client.eligPhone || null,
                    org: this.client.eligOrg || null,
                })
                this.client.eligSent = true
                this.$emit('change')
            } catch (e) {
                this.$alert({
                    message: (e && e.message) ? e.message : 'We couldn’t send your request. Please try again.',
                    variant: 'danger',
                })
            } finally {
                this.eligSubmitting = false
            }
        },
        // Make a failed-lookup message impossible to miss: wait for it to render,
        // then scroll it into view and move focus to it (screen readers announce
        // it via role/alert too). Guards against the user having scrolled down to
        // the button when the alert renders higher up in the card.
        revealLookupError () {
            this.$nextTick(() => {
                const el = this.$refs.lookupErr
                if (!el) return
                if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                if (el.focus) el.focus()
            })
        },
        // Same masking AboutDemographicBlock applies — (xxx) xxx-xxxx.
        formatPhone (e) {
            const d = String(e.target.value || '').replace(/\D/g, '').slice(0, 10)
            let v = d
            if (d.length >= 4 && d.length < 7) v = `(${d.slice(0, 3)}) ${d.slice(3)}`
            else if (d.length >= 7) v = `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
            this.client.phone = v
        },
        async doLookup () {
            this.lookupError = ''
            this.client.formerIchra = false
            this.lookupLoading = true
            try {
                const dob = `${this.client.lookupDobY}-${String(this.client.lookupDobM).padStart(2, '0')}-${String(this.client.lookupDobD).padStart(2, '0')}`
                const resp = await this.$api.getExistingClientToken({
                    email: this.client.lookupEmail,
                    dob,
                    slug: this.slug || undefined,
                    aep: this.aep,
                })
                if (resp?.error || resp?.detail) {
                    const e = resp.error || resp.detail
                    this.lookupError = typeof e === 'string'
                        ? e
                        : (e?.message || 'We couldn’t find an account matching that email and date of birth.')
                    this.revealLookupError()
                    return
                }
                if (resp?.former_ichra) {
                    // Former Benefit Bay client (closed 2026-10-01): the account
                    // exists but is closed. No token, no name, no methods -- the
                    // block message below explains, and validate() keeps Next shut.
                    this.client.formerIchra = true
                    this.client.allowed_methods = []
                    this.client.allowedMethodsResolved = true
                    this.client.lookupFound = true
                    this.$emit('change')
                    return
                }
                // Hydrate
                this.client.token = resp.token
                this.client.first_name = resp.first_name
                this.client.last_name = resp.last_name
                this.client.preferred_name = resp.preferred_name
                this.client.email = this.client.lookupEmail
                this.client.dob = dob
                this.client.dobM = this.client.lookupDobM
                this.client.dobD = this.client.lookupDobD
                this.client.dobY = this.client.lookupDobY
                this.client.zip_code = resp.zip_code
                this.client.fips = resp.fips
                this.client.effective_date = resp.effective_date
                this.client.group = resp.group
                this.client.group_relationship = resp.group_relationship
                this.client.source_type = resp.source_type
                this.client.source_name = resp.source_name
                this.client.allowed_methods = resp.allowed_methods || []
                this.client.allowedMethodsResolved = true
                this.client.agent = resp.agent
                this.client.agents = resp.agents
                this.client.soa60days = resp.soa60days
                this.client.hasPhoneOnFile = !!resp.has_phone
                // #679 Phase 2: the PII read-back echo — the server includes
                // these keys ONLY when this session is already signed in as
                // the matched client, or as staff (#686 tracks whether
                // token-only visitors will be asked to log in). addressEchoed
                // unlocks the confirm step's address row.
                this.$set(this.client, 'addressEchoed', 'street_1' in resp)
                if ('street_1' in resp) {
                    this.client.street_1 = resp.street_1 || ''
                    this.client.street_2 = resp.street_2 || ''
                    this.client.city = resp.city || ''
                    this.$set(this.client, 'state', resp.state || '')
                    this.$set(this.client, 'has_full_address', !!resp.has_full_address)
                    if (resp.phone && !this.client.phone) this.client.phone = resp.phone
                }
                // #576: soonest existing upcoming appointment ({datetime,
                // manage_token}) or null. The Time step warns and blocks a
                // duplicate booking when this is set.
                this.client.upcoming_appointment = resp.upcoming_appointment || null
                // #679: returning does not mean enrolled (a client seen last
                // year at 63 who deferred because they were still working is
                // returning and new to Medicare) — only pre-select the AEP
                // screener when we authoritatively know there's an active
                // policy, and never clobber an existing answer (resume flow).
                this.$set(this.client, 'enrolled', !!resp.enrolled)
                if (this.aep && resp.enrolled && this.client.aep_reason == null) {
                    this.$set(this.client, 'aep_reason', 'plan_review')
                }
                this.client.lookupFound = true
                this.$emit('change')
            } catch (err) {
                this.lookupError = err?.message || 'We couldn’t find that account. Double-check the email and date of birth, or that you’re using the email you signed up with.'
                this.revealLookupError()
            } finally {
                this.lookupLoading = false
            }
        },
    },
}
